import { useState } from 'react';

import { Box, Flex, Stack, useTheme } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  removeAddons,
  removeProvider,
  setAddons,
  setProvider
} from 'store/reducers/bookingAppointmentSlice';

import { ExtendedAddonInterface, ProviderInterface } from 'types';

import { CustomScrollBar } from 'components';
import { ProviderHeader, ProviderItem } from './components';

interface ProvidersListProps {
  locationId: number;
  selectedProviders: Record<number, ProviderInterface | null> | null;
  onRemoveService: (id: number) => void;
  onToggleProvider: (serviceId: number, provider: ProviderInterface) => void;
}

const ProvidersList = ({
  locationId,
  selectedProviders,
  onToggleProvider,
  onRemoveService
}: ProvidersListProps) => {
  const dispatch = useAppDispatch();
  const {
    colors: {
      palette: {
        booking: {
          stepService: { providerList: colors }
        }
      }
    }
  } = useTheme();
  const { providers } = useAppSelector((state) => state.services);
  const { selectedServices, selectedAddons, moveApptId } = useAppSelector(
    (state) => state.bookingAppointment
  );
  const isAllowMultipleSelect = Object.values(selectedProviders || {}).every(
    (item) => item?.id === -1
  );
  const [isInitialSelect, setIsInitialSelect] = useState(isAllowMultipleSelect);

  const currentProviders = providers[locationId];
  const isOneProviderList = selectedServices.length === 1;

  const handleProviderClick =
    (serviceId: number, provider: ProviderInterface, isFirstBlock: boolean) =>
    () => {
      onToggleProvider(serviceId, provider);

      if (isInitialSelect && isFirstBlock) {
        selectedServices.forEach((service) => {
          if (service.id !== serviceId) {
            const availableProviders = currentProviders[service.id];
            const isProviderAvailable = availableProviders.some(
              (p) => p.id === provider.id
            );
            if (isProviderAvailable) {
              dispatch(setProvider({ service: service.id, provider }));
            }
          }
        });
        setIsInitialSelect(false);
      }
    };

  const handleRemoveService = (id: number) => () => {
    onRemoveService(id);
  };

  const updateAddons = (
    updatedAddons: ExtendedAddonInterface[],
    serviceId: number
  ) => {
    if (updatedAddons.length === 0) {
      dispatch(removeAddons({ service: serviceId }));
    } else {
      dispatch(setAddons({ service: serviceId, addons: updatedAddons }));
    }
  };

  const handleRemoveAddon = (item: ExtendedAddonInterface) => () => {
    const updatedAddons = selectedAddons[item.parentServiceId].filter(
      (addon) => addon.id !== item.id
    );
    updateAddons(updatedAddons, item.parentServiceId);
    dispatch(removeProvider({ service: item.id }));
  };

  return (
    <Stack gap='10px'>
      {selectedServices.map((service, index) => {
        const currentProvider =
          selectedProviders && selectedProviders?.[service?.id];
        const providerList = currentProviders?.[service?.id];
        const addonsArray = selectedAddons?.[service?.id];
        const isAddonsExist = addonsArray && addonsArray?.length > 0;
        const isAlternativeStyle = isOneProviderList && !isAddonsExist;
        const isFirstBlock = index === 0;

        return (
          <Stack key={service?.id} gap='10px'>
            <Box mb='0'>
              <Box
                py='15px'
                px='18px'
                bg={colors.headerBg}
                borderTopRadius='4px'
              >
                <ProviderHeader
                  serviceName={service?.name}
                  onRemoveService={handleRemoveService(service?.id)}
                  isRemoveDisabled={!!moveApptId}
                />
              </Box>

              <CustomScrollBar
                maxHeight={isAlternativeStyle ? '2000px' : '327px'}
              >
                <Flex gap='4px' flexWrap='wrap' mt='4px'>
                  {providerList?.map((provider) => {
                    const isChecked = currentProvider?.id === provider.id;

                    return (
                      <ProviderItem
                        key={provider.id}
                        provider={provider}
                        onClickProvider={handleProviderClick(
                          service.id,
                          provider,
                          isFirstBlock
                        )}
                        isChecked={isChecked}
                        isDisabled={false}
                      />
                    );
                  })}
                </Flex>
              </CustomScrollBar>
            </Box>

            {isAddonsExist &&
              addonsArray.map((item) => {
                const currentAddonProvider =
                  selectedProviders && selectedProviders[item.id];

                return (
                  <Box mb='0' key={item.id}>
                    <Box
                      py='15px'
                      px='18px'
                      bg={colors.headerBg}
                      borderTopRadius='4px'
                    >
                      <ProviderHeader
                        serviceName={item.name}
                        onRemoveService={handleRemoveAddon(item)}
                        isRemoveDisabled={!!moveApptId}
                      />
                    </Box>

                    <Flex gap='4px' flexWrap='wrap' mt='4px'>
                      {providerList?.map((provider) => {
                        const isChecked =
                          currentAddonProvider?.id === provider.id;

                        return (
                          <ProviderItem
                            key={provider.id}
                            provider={provider}
                            onClickProvider={handleProviderClick(
                              item.id,
                              provider,
                              false
                            )}
                            isChecked={isChecked}
                            isDisabled={false}
                          />
                        );
                      })}
                    </Flex>
                  </Box>
                );
              })}
          </Stack>
        );
      })}
    </Stack>
  );
};

export default ProvidersList;
